import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle,
  CircleAlert,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useStockTransactions,
  useDeleteStockTransaction,
} from "@/hooks/useStockTransactions";
import { useLocation } from "wouter";
import LoadingIndicator from "@/components/LoadingIndicator";
import type { TransactionType } from "@/types/stockTran";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cleanupBodyLock = () => {
  document.body.style.removeProperty("pointer-events");
  document.body.style.removeProperty("overflow");
};

export const StockTransactionList = () => {
  const { format: formatDecimal } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();

  // Filters
  const [voucherType, setVoucherType] = useState<TransactionType | "ALL">(
    "ALL"
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoucherNumber, setSelectedVoucherNumber] = useState<
    string | null
  >(null);

  // Fetch all data without search filter
  const filters = {
    ...(voucherType !== "ALL" && { voucherType }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    page: 1,
    limit: 100,
  };

  const { data, isLoading, error } = useStockTransactions(filters);

  // Search across all fields
  const filteredTransactions = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((transaction: any) =>
      Object.values(transaction).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [data?.data, search]);

  // Paginate the filtered results
  const paginatedTransactions = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, page, limit]);

  const totalPages = Math.ceil(filteredTransactions.length / limit);
  const deleteTransaction = useDeleteStockTransaction();

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Cleanup body lock on dialog open/close
  useEffect(() => {
    cleanupBodyLock();
    return cleanupBodyLock;
  }, [deleteDialogOpen]);

  const handleDelete = async () => {
    if (!selectedVoucherNumber) return;

    try {
      await deleteTransaction.mutateAsync(selectedVoucherNumber, {
        onSuccess: () => {
          toast({
            title: "Voucher Deleted",
            description: "Stock voucher has been deleted successfully",
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });
          setDeleteDialogOpen(false);
          setSelectedVoucherNumber(null);
        },
        onError: (error: any) => {
          toast({
            title: "Failed to Delete Voucher",
            description: error.response?.data?.message || error.message,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
            duration: 4000,
          });
        },
      });
    } catch (error) {
      console.error("Error deleting voucher:", error);
    }
  };

  const getTypeColor = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700";
      case "GO":
        return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700";
      case "INV":
        return "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700";
      case "DO":
        return "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  const getTypeLabel = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return "Stock In";
      case "GO":
        return "Stock Out";
      case "INV":
        return "Invoice (OUT)";
      case "DO":
        return "Delivery Order";
      default:
        return type;
    }
  };

  const getTypeIcon = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return <ArrowDownToLine className="h-4 w-4" />;
      case "GO":
      case "INV":
      case "DO":
        return <ArrowUpFromLine className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const handleResetFilters = () => {
    setVoucherType("ALL");
    setDateFrom("");
    setDateTo("");
    setSearch("");
    setPage(1);
  };

  if (isLoading) return <LoadingIndicator />;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          Error loading transactions: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stock Movement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View all stock transactions and movements
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/stock-in")}
            className="bg-green-600 hover:bg-green-700"
          >
            <ArrowDownToLine className="h-4 w-4 mr-2" />
            Stock In
          </Button>
          <Button
            onClick={() => navigate("/stock-out")}
            className="bg-red-600 hover:bg-red-700"
          >
            <ArrowUpFromLine className="h-4 w-4 mr-2" />
            Stock Out
          </Button>
          <Button onClick={() => navigate("/stock-history")} variant="outline">
            <Eye className="h-4 w-4 mr-2" />
            Stock History
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Filters</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="voucherType">Transaction Type</Label>
                <Select
                  value={voucherType}
                  onValueChange={(value) =>
                    setVoucherType(value as TransactionType | "ALL")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Types</SelectItem>
                    <SelectItem value="GI">Stock In (GI)</SelectItem>
                    <SelectItem value="GO">Stock Out (GO)</SelectItem>
                    <SelectItem value="INV">Invoice Out (INV)</SelectItem>
                    <SelectItem value="DO">Delivery Order (DO)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dateFrom">Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="dateTo">Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search all fields..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent className="pt-6">
          {paginatedTransactions && paginatedTransactions.length > 0 ? (
            <>
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead>Voucher Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total Quantity</TableHead>
                    <TableHead className="text-right">
                      Total Cost/Sales (RM)
                    </TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransactions.map((transaction: any) => (
                    <TableRow key={transaction.voucherNumber}>
                      <TableCell className="font-medium">
                        {transaction.voucherNumber}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getTypeColor(
                            transaction.type
                          )} flex items-center gap-1 w-fit`}
                          variant="outline"
                        >
                          {getTypeIcon(transaction.type)}
                          {getTypeLabel(transaction.type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(transaction.entryDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm text-gray-600">
                          {transaction.itemCount}{" "}
                          {transaction.itemCount === 1 ? "item" : "items"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            transaction.type === "GI"
                              ? "text-green-600 font-medium"
                              : "text-red-600 font-medium"
                          }
                        >
                          {transaction.type === "GI" ? "+" : "-"}
                          {formatDecimal(Number(transaction.totalQuantity || 0))}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatDecimal(Number(
                          transaction.totalAmountIn ||
                            transaction.totalAmountOut ||
                            0
                        ))}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {transaction.reference || "-"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                if (transaction.type === "GI") {
                                  navigate(`/stock-in/view`, {
                                    state: {
                                      voucherNumber: transaction.voucherNumber,
                                    },
                                  });
                                } else {
                                  navigate(`/stock-out/view`, {
                                    state: {
                                      voucherNumber: transaction.voucherNumber,
                                    },
                                  });
                                }
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedVoucherNumber(
                                  transaction.voucherNumber
                                );

                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {filteredTransactions.length > limit && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, filteredTransactions.length)} of{" "}
                    {filteredTransactions.length} vouchers
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        Page {page} of {totalPages}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <div className="mb-4">
                <Search className="h-12 w-12 mx-auto text-gray-300" />
              </div>
              <p className="text-lg font-medium">No transactions found</p>
              <p className="text-sm mt-2">
                {search || dateFrom || dateTo || voucherType !== "ALL"
                  ? "Try adjusting your filters"
                  : "Create your first stock transaction to get started"}
              </p>
              {!search && !dateFrom && !dateTo && voucherType === "ALL" && (
                <div className="flex gap-2 justify-center mt-4">
                  <Button
                    onClick={() => navigate("/stock-in/add")}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <ArrowDownToLine className="h-4 w-4 mr-2" />
                    Create Stock In
                  </Button>
                  <Button
                    onClick={() => navigate("/stock-out/add")}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <ArrowUpFromLine className="h-4 w-4 mr-2" />
                    Create Stock Out
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will delete the stock voucher and all items within it,
              and reverse its effects on inventory quantities. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setSelectedVoucherNumber(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteTransaction.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
